import { useTranslation } from "react-i18next";
import { statusCategories } from "@Components/PortalSetings/Configuration/PortalLevelStatus/StatusConstants";
import { ReactFlow, Controls, Background, MarkerType } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import dagre from "dagre";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { showToastNotification } from "../../../../../actions/toast";
import { TOAST_TYPE_ERROR } from "../../../../../services/constants";
import { getStatusListInTransitions, getTransitionsList, setWorkflowResourceType } from "../WorkflowHelper";
import NoTransitionIllustartion from "../../../../../assets/icons/NoTransitionIllustration.svg";

import css from "../Workflows.module.css";
import { entityData } from "../WorkflowConstants";
import DropDownListComponent from "../../../../../cubeComponent/form/DropdownListComponent";

function TransitionsDiagram({ workflowId }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const entityRef = useRef(null);

  const resourceType = useSelector((state) => state.workflowResourceType);

  const [nodes, setNodes] = useState([]);
  // const [connectors, setConnectors] = useState([]);
  const [edges, setEdges] = useState([]);
  const [isLoadingComplete, setIsLoadingComplete] = useState(false);

  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  const nodeWidth = 172;
  const nodeHeight = 36;

  const getLayoutedElements = (nodes, edges) => {
    dagreGraph.setGraph({ rankdir: "TB" });

    nodes.forEach((node) => {
      dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
    });

    edges.forEach((edge) => {
      dagreGraph.setEdge(edge.source, edge.target);
    });

    dagre.layout(dagreGraph);

    const layoutedNodes = nodes.map((node) => {
      const nodeWithPosition = dagreGraph.node(node.id);
      node.position = {
        x: nodeWithPosition.x - nodeWidth / 2,
        y: nodeWithPosition.y - nodeHeight / 2,
      };
      return node;
    });

    return { nodes: layoutedNodes, edges };
  };

  const connectorStrokeColor = "#666A84";

  const onChangeEntity = (args) => {
    dispatch(setWorkflowResourceType(args.value));
    setIsLoadingComplete(false);
  };

  const getNodeColor = (category) => {
    const matchedCategory = statusCategories.find((record) => record.name === category);
    const backgroundColor = matchedCategory ? matchedCategory.backgroundHexcode : "#0006A2";
    return backgroundColor;
  };

  const processTransitions = (transitions, statuses) => {
    const nodes = [];
    const edges = [];

    const fromStatusIds = transitions.map((t) => t.statusFrom._id);
    const toStatusIds = transitions.map((t) => t.statusTo._id);

    statuses.map((status) => {
      let nodeType = "default";
      if (fromStatusIds.includes(status._id) && !toStatusIds.includes(status._id)) {
        nodeType = "input";
      } else if (!fromStatusIds.includes(status._id) && toStatusIds.includes(status._id)) {
        nodeType = "output";
      }

      nodes.push({
        id: status._id,
        type: nodeType,
        data: { label: status.name },
        category: status.category,
      });
    });

    transitions.map((transition) => {
      edges.push({
        id: `${transition.statusFrom._id}+${transition.statusTo._id}`,
        source: transition.statusFrom._id,
        target: transition.statusTo._id,
        label: transition.name,
        type: "step",
        markerEnd: {
          type: MarkerType.ArrowClosed,
          width: 20,
          height: 20,
          color: connectorStrokeColor,
        },
      });
    });

    nodes.map((node) => {
      const nodeColor = getNodeColor(node.category);
      node.style = {
        backgroundColor: nodeColor,
        color: "white",
        border: "0px",
      };
    });

    return {
      nodes,
      edges,
    };
  };

  const fetchTransitionsData = async () => {
    const response = await getTransitionsList({
      value: { params: { workflowId, resourceType } },
    });
    return response.data;
  };

  const fetchStatusListInTransitions = async () => {
    const response = await getStatusListInTransitions({
      workflowId,
      resourceType,
    });
    return response.data;
  };

  const fetchData = async () => {
    const transitions = await fetchTransitionsData();
    const statuses = await fetchStatusListInTransitions();

    if (transitions.status && statuses.status) {
      // const statusNodes = getStatusNodes(statuses.result);
      // const transitionConnectors = getTransitionConnectors(transitions.result);

      // setConnectors(transitionConnectors);
      // setNodes(statusNodes);
      const transitionEdges = processTransitions(transitions.result, statuses.result);
      const layoutElements = getLayoutedElements(transitionEdges.nodes, transitionEdges.edges);
      setEdges(layoutElements.edges);
      setNodes(layoutElements.nodes);
      setIsLoadingComplete(true);
      return;
    }

    dispatch(
      showToastNotification({
        content: t("Internal Server Error"),
        type: TOAST_TYPE_ERROR,
      }),
    );
  };

  useEffect(() => {
    fetchData();
  }, [resourceType]);

  const getIllustration = () => (
    <div className={css.transitionsIllustrationDiagram}>
      <img src={NoTransitionIllustartion} />
    </div>
  );

  // const getDiagramComponent = useCallback(() => (
  //   <DiagramComponent
  //     ref={diagramRef}
  //     id="transitionsDiagram"
  //     nodes={nodes}
  //     layout={diagramLayout}
  //     connectors={connectors}
  //     snapSettings={snapSettings}
  //     getNodeDefaults={getNodeDefaults}
  //     getConnectorDefaults={getConnectorDefaults}
  //     constraints={DiagramConstraints.Default | DiagramConstraints.LineRouting}
  //     click={connectorOnClick}
  //   >
  //     <Inject services={[DataBinding, MindMap, HierarchicalTree, LineRouting, SymmetricLayout, ComplexHierarchicalTree, LineDistribution]} />
  //   </DiagramComponent>
  // ), [nodes, connectors]);

  const getDiagramComponent = useCallback(
    () => (
      <div
        className="w-full h-full border-s border-e border-b"
        style={{
          borderColor: "rgb(236 243 254 / var(--tw-border-opacity, 1))",
        }}
      >
        <ReactFlow nodes={nodes} edges={edges} fitView proOptions={{ hideAttribution: true }}>
          <Background />
          <Controls />
        </ReactFlow>
      </div>
    ),
    [nodes, edges],
  );

  return (
    <div className={css.transitionsDiagramContainer}>
      <div className={css.transitionDiagramWrapper}>
        <div className="flex pt-5 ps-8 pb-5 border border-solid border-b-2 border-button_light bg-system_colour_43">
          <DropDownListComponent
            ref={entityRef}
            dataSource={entityData.dataSource}
            fields={{ text: "name", value: "value" }}
            onChange={onChangeEntity}
            value={resourceType}
          />
        </div>
        {isLoadingComplete && edges.length ? getDiagramComponent() : undefined}
        {isLoadingComplete && !edges.length ? getIllustration() : undefined}
      </div>
    </div>
  );
}

export default TransitionsDiagram;
